import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, ShieldCheck, Layers, Megaphone, Check, ArrowUpRight } from "lucide-react";

const services = [
  {
    id: "ai-hybrid",
    tag: "01",
    icon: <Bot size={28} />,
    title: "AI + Human Hybrid Development",
    lead: "Our signature practice. Senior engineers architect; AI accelerates the interior. You get enterprise-grade applications in a fraction of the time — without the drift.",
    offers: [
      "MVPs shipped in 2–4 weeks",
      "Enterprise platforms in 8–12 weeks",
      "Full backend, frontend, DevOps",
      "Architecture review on every sprint",
    ],
    outcomes: ["5x faster time-to-production", "Senior engineering on every line", "Measurable, auditable progress"],
  },
  {
    id: "enterprise",
    tag: "02",
    icon: <Layers size={28} />,
    title: "Web & Enterprise Software",
    lead: "Portals, SaaS products, ERPs, event-driven platforms. We build systems designed to scale past the first customer — and survive the 100th.",
    offers: [
      "Multi-tenant architecture",
      "Event-sourced / CQRS backends",
      "React / Next.js frontends",
      "Cloud-native on AWS / GCP / Azure",
    ],
    outcomes: ["Handles millions of events/day", "Zero-downtime deploys", "Compliance-ready from day one"],
  },
  {
    id: "cybersecurity",
    tag: "03",
    icon: <ShieldCheck size={28} />,
    title: "Cybersecurity Platforms",
    lead: "Threat-intel consoles, SOC tooling, zero-trust access, hardened backends. Built to standards. Tested by adversaries.",
    offers: [
      "SOC dashboards & threat intel",
      "Zero-trust auth & RBAC",
      "Pen-test hardened delivery",
      "SOC2 / ISO / HIPAA alignment",
    ],
    outcomes: ["62% fewer false positives (Vantage)", "MTTR cut in half", "Pass critical audits on first attempt"],
  },
  {
    id: "marketing",
    tag: "04",
    icon: <Megaphone size={28} />,
    title: "Brand, Digital Marketing & Establishment",
    lead: "Brand systems, launches, performance funnels, SEO. We treat marketing like engineering: measurable, compounding, accountable.",
    offers: [
      "Brand identity & systems",
      "Performance marketing & funnels",
      "SEO / content engines",
      "Launch strategy & PR",
    ],
    outcomes: ["+38% qualified leads (Lumen)", "2.4x average ROAS lift", "Full-funnel analytics baked in"],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section className="relative border-b border-white/10 bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 topo-grid opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="overline">Services</p>
            </div>
            <div className="md:col-span-8">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-[-0.03em] leading-[0.95]" data-testid="services-title">
                Four practices.<br/>
                <span className="text-[#ff3333]">One delivery engine.</span>
              </h1>
              <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
                Each practice runs on the same Ridgeline Method — senior engineers paired with a disciplined AI toolchain. Pick a practice. Or combine several. We scope, sign and start inside a week.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 space-y-16 md:space-y-24">
          {services.map((s, idx) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 pb-16 border-b border-white/5 last:border-b-0"
              data-testid={`service-row-${s.id}`}
            >
              <div className="md:col-span-4">
                <div className="sticky top-28">
                  <div className="flex items-center gap-3 text-[#ff3333]">
                    <span className="mono text-sm font-bold">{s.tag}</span>
                    <span className="w-8 h-[1px] bg-[#ff3333]" />
                    <span>{s.icon}</span>
                  </div>
                  <h2 className="mt-6 font-display text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight">{s.title}</h2>
                </div>
              </div>

              <div className="md:col-span-8">
                <p className="text-xl text-zinc-300 leading-relaxed font-display font-medium">{s.lead}</p>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border border-white/10 p-6">
                    <p className="overline text-zinc-500">Engagements</p>
                    <ul className="mt-4 space-y-3">
                      {s.offers.map((o) => (
                        <li key={o} className="flex items-start gap-3 text-sm text-zinc-300">
                          <Check size={16} className="text-[#ff3333] mt-0.5 shrink-0" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-[#111] border border-white/10 p-6">
                    <p className="overline text-zinc-500">Outcomes</p>
                    <ul className="mt-4 space-y-3">
                      {s.outcomes.map((o) => (
                        <li key={o} className="flex items-start gap-3 text-sm text-white">
                          <span className="w-1.5 h-1.5 bg-[#ff3333] mt-1.5 shrink-0" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] border-t border-white/10 py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h2 className="font-display text-3xl md:text-4xl font-black text-white tracking-tight leading-tight">
            Need a combined engagement? <span className="text-[#ff3333]">We do that too.</span>
          </h2>
          <Link to="/contact" className="btn-primary" data-testid="services-cta">
            Scope a project <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
